/* eslint-disable no-console */
import { faChevronDown, faChevronUp } from "@awesome.me/kit-cae4660dd3/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ContentReader, { ISegment } from "@imaginelearning/mps-ctk-reader-wrapper";
import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import {
  IActivity,
  IEducatorPreviewerProps,
  Languages,
  ServiceStatusEnum,
  defaultDoolittleVersion
} from "../educator-content-reader.props";
import Avatar from "../../avatar/avatar";
import AvatarEnum from "../../../enums/avatar.enum";
import AvatarPriorityEnum from "../../../enums/avatar-priority.enum";
import SizeExtendedEnum from "../../../enums/size-extended.enum";
import ThemesEnum from "../../../enums/themes.enum";
import LanguageEnum from "../../../enums/language.enum";
import { removeAllCustomArrayMethods } from "../../../helpers/removeAllCustomArrayMethods";
import { useTranslation } from "react-i18next";
import Footer from "./footer/footer";
import EducatorContentSpinner from "../educator-content-spinner";
import "../educator-content-reader.scss";
import "./educator-previewer.scss";

declare global {
  interface Window {
    ContentReader: any;
  }
}
window.ContentReader = window.ContentReader || {};

const EducatorPreviewer = ({
  lessonTitle,
  doolittleBaseMediaUrl,
  doolittleBaseApiUrl,
  activities,
  selectedIndex,
  setMinHeight,
  spanishAvailable,
  previewLanguage,
  handleGetActivities,
  getActivityToken,
  itemEngineAuthorUri,
  itemEngineItemsUri
}: IEducatorPreviewerProps) => {
  const [selectedActivity, setSelectedActivity] = useState({
    ...activities[selectedIndex || 0],
    index: selectedIndex || 0
  });
  const [tokenState, setTokenState] = useState({
    status: ServiceStatusEnum.LOADING,
    payload: "",
    showPlayer: false
  });

  const [selectedLanguage, setSelectedLanguage] = useState(previewLanguage);
  const initialSideKickMessageState = {
    show: false,
    msg: "",
    userDismissed: false,
    questionId: ""
  };
  const { t } = useTranslation();
  const [sideKickMessage, setSideKickMessage] = useState(initialSideKickMessageState);
  const defaultSidekickMessage = "Try Again";
  const [retryMessage, setRetryMessage] = useState(defaultSidekickMessage);
  const [retryButton, setRetryButton] = useState(false);

  const [down, setDown] = React.useState(true);
  const handleToggleClick = () => {
    setDown(!down);
  };
  const handleChevronIcon = () => {
    if (down) {
      return <FontAwesomeIcon icon={faChevronDown} />;
    } else {
      return <FontAwesomeIcon icon={faChevronUp} />;
    }
  };

  const handleLanguageSelect = (key: string) => {
    if (key === selectedLanguage) return; // do nothing if the language is already selected
    const language = key === Languages[LanguageEnum.English] ? LanguageEnum.English : LanguageEnum.Spanish;
    setTokenState({
      status: ServiceStatusEnum.LOADING,
      payload: "",
      showPlayer: false
    });
    setSelectedLanguage(key);
    handleGetActivities(language);
  };

  const handleActivitySelect = (key: string | null) => {
    const index = parseInt(key as string, 10);
    if (index > -1) {
      if (index !== selectedActivity.index) {
        window.ContentReader.destroy();
      }
      setSelectedActivity({ ...activities[index], index });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const nextSectionTokenResult = await getActivityToken(
        selectedActivity.pathId,
        selectedActivity.pathVersion,
        selectedActivity.takeId || "",
        selectedActivity.isMasteryCheck,
        selectedActivity.lessonAttemptId || ""
      );

      if (nextSectionTokenResult.status === ServiceStatusEnum.LOADED) {
        if (nextSectionTokenResult.payload) {
          const showPlayer = true;
          const { status, payload } = nextSectionTokenResult;
          setTokenState({ status, payload, showPlayer });
        }
      }
    };
    if (selectedActivity.pathId) {
      setTokenState({
        status: ServiceStatusEnum.LOADING,
        payload: "",
        showPlayer: false
      });
      fetchData();
    }
  }, [selectedActivity.pathId, selectedActivity.index, selectedActivity.takeId]);

  useEffect(() => {
    const index = selectedActivity.index;
    setSelectedActivity({ ...activities[index], index });
  }, [activities, selectedActivity.index]);

  const handleTryagainClick = () => {
    setRetryButton(false);
    setSideKickMessage({ ...sideKickMessage, show: false, userDismissed: true });
  };

  const handleSideKickMessageUpdate = (segment: ISegment, closeOverrride: boolean) => {
    setRetryButton(false);
    if (closeOverrride && sideKickMessage.show && !sideKickMessage.userDismissed) {
      setSideKickMessage({
        show: false,
        msg: retryMessage,
        userDismissed: true,
        questionId: ""
      });
      return;
    }
    const question = segment.components.find((item: any) => item.type === "Question" || item.type === "QuestionItem");
    if (question) {
      const { maxAttempts, state: questionState } = question;
      if (questionState.attempts === maxAttempts) {
        setRetryButton(false);
        setSideKickMessage({
          show: false,
          msg: retryMessage,
          userDismissed: true,
          questionId: ""
        });
      }
      if (
        questionState.attempts !== 0 &&
        questionState.correct !== null &&
        questionState.correct === false &&
        !sideKickMessage.userDismissed
      ) {
        if (questionState.attempts && maxAttempts && questionState.attempts < maxAttempts) {
          //  feedbackCompleted refers to the current attemptCount, so when attemptCount is 1 and feedbackCompleted is true that means feedback has been watched for attempt 1.
          if (questionState.attempts === 1 && !questionState.feedbackCompleted) {
            // question attempted once, feedback not watched
            setRetryButton(true);
          }

          setSideKickMessage({
            show: !!questionState.feedbackCompleted,
            msg: retryMessage,
            userDismissed: false,
            questionId: question.id
          });
          if (questionState.feedbackCompleted) {
            setTimeout(() => {
              setSideKickMessage({ ...sideKickMessage, show: false, userDismissed: true });
            }, 6000);
          }
        }
      } else {
        setRetryButton(false);
        setSideKickMessage({
          show: false,
          msg: retryMessage,
          userDismissed: false,
          questionId: question.id
        });
      }
    }
  };

  useEffect(() => {
    setSideKickMessage({
      ...sideKickMessage,
      msg: retryMessage
    });
  }, [retryMessage]);

  useEffect(() => {
    //the Assignment Builder MFE uses a module that adds custom methods to the Array prototype.
    //To prevent these custom methods from polluting our IN loops, we remove them here.
    return () => {
      removeAllCustomArrayMethods();
    };
  }, []);

  return (
    <div
      className="educator-content-reader-preview lesson-previewer"
      style={setMinHeight ? { minHeight: "900px" } : {}}
    >
      <div className="h-auto w-100 mb-2 row-dropdowns">
        <Dropdown
          role="menu"
          className="activity-dropdown"
          onSelect={handleActivitySelect}
          onClick={handleToggleClick}
          drop="down"
        >
          <Dropdown.Toggle id="activity-dropdown">
            {selectedActivity.type} {handleChevronIcon()}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {activities.map((item: IActivity, index: number) => {
              const selectedItem = index === selectedActivity.index;
              return (
                <Dropdown.Item key={index} active={selectedItem} eventKey={index.toString()} disabled={false}>
                  {item.type}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
        {spanishAvailable && (
          <Dropdown
            role="menu"
            className="activity-dropdown"
            onSelect={handleLanguageSelect}
            onClick={handleToggleClick}
            drop="down"
          >
            <Dropdown.Toggle id="activity-dropdown">
              {selectedLanguage || Languages[LanguageEnum.English]} {handleChevronIcon()}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {Languages.map((item: string, index: number) => {
                const selectedItem = index === Languages.indexOf(selectedLanguage || Languages[LanguageEnum.English]);
                return (
                  <Dropdown.Item key={index} active={selectedItem} eventKey={item} disabled={false}>
                    {item}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
      <div className="preview-banner">
        <span className="preview-lesson-title">{lessonTitle}</span>
        <span className="preview-activity-type">{t(`${selectedActivity.type}`)}</span>
      </div>
      <div className="preview-content-player">
        {!tokenState.showPlayer ? (
          <EducatorContentSpinner />
        ) : (
          <ContentReader
            token={tokenState.payload}
            doolittleBaseMediaUrl={doolittleBaseMediaUrl}
            doolittleBaseApiUrl={doolittleBaseApiUrl}
            doolittleVersion={defaultDoolittleVersion}
            displayCorrectAnswer={true}
            shouldAutoPlay={false}
            isSpanishContent={selectedLanguage === Languages[LanguageEnum.Spanish]}
            itemEngineAuthorUri={itemEngineAuthorUri}
            itemEngineItemsUri={itemEngineItemsUri}
            scrollButtonText={t("Scroll")}
            disableFastForward={false}
            disableAnnotations={true}
            isAudioCompletionRequired={false}
          />
        )}
      </div>
      {sideKickMessage.show && !sideKickMessage.userDismissed && (
        <div
          className="sidekick-message"
          onClick={handleTryagainClick}
          role="button"
          tabIndex={0}
          onKeyDown={() => handleTryagainClick()}
        >
          <span className="sidekick-label">{`${sideKickMessage.msg}`}</span>
          <Avatar
            avatar={AvatarEnum.JOE}
            avatarType={AvatarPriorityEnum.DEFAULT}
            size={SizeExtendedEnum.X3}
            theme={ThemesEnum.STUDENT_3_5}
          />
        </div>
      )}
      <Footer
        activityPathId={selectedActivity.pathId}
        isMasteryCheck={!!selectedActivity.isMasteryCheck}
        isRetryVisible={!!retryButton}
        sideKickMessageCallback={handleSideKickMessageUpdate}
      />
    </div>
  );
};

export default EducatorPreviewer;
